import React from 'react'
import { Paragraph, Table, TableRow, TableCell, PageBreak, HeadingLevel, WidthType } from "docx";
import dayjs, { Dayjs } from 'dayjs'
import language from "../../../resources/languages/en_US"

// Interfaces
import { Tender as TenderType } from "../../../interfaces/pages/opportunities"

// Commons
import { exportDoc } from "../../../commons/export-doc";

const { App: { title }, Pages: { Opportunities: { Tender } }, Share, Enum: EnumLanguage } = language

export const Proposal = async (tender: TenderType) => {

    const submissionDeadline = (tender?.proposal?.Proposal_Template?.Cover_Page?.Submission_Deadline && dayjs(tender?.proposal?.Proposal_Template?.Cover_Page?.Submission_Deadline).isValid()) ? dayjs(tender?.proposal?.Proposal_Template?.Cover_Page?.Submission_Deadline).tz(process.env.REACT_APP_TZ).format('MMMM D, YYYY') : Share.notSpecified;
    const specificDeliverables = tender?.proposal?.Proposal_Template?.Scope_of_Work?.Specific_Deliverables?.map((item) => new Paragraph("- " + item)) || [];
    const billOfMaterialsTable = tender?.proposal?.Proposal_Template?.Bill_of_Materials?.Table?.map(({ Item = '', Quantity = '', Preferred_Vendor = '', Model_Number = '', Technical_Specification = [], Unit_Price = '', Delivery_Time = ''}) => new TableRow({
        children: [
            new TableCell({ children: [new Paragraph(Item)] }),
            new TableCell({ children: [new Paragraph(Quantity)] }),
            new TableCell({ children: [new Paragraph(Preferred_Vendor)] }),
            new TableCell({ children: [new Paragraph(Model_Number)] }),
            new TableCell({ children: [new Paragraph(Technical_Specification.join(','))] }),
            new TableCell({ children: [new Paragraph(Tender.toBeProvidedByContractor)] }),
            new TableCell({ children: [new Paragraph(Tender.toBeProvidedByContractor)] }),
        ],
    })) || [];
    const standardsAndCompliance = tender?.proposal?.Proposal_Template?.Technical_Specifications_Compliance?.Standards_and_Compliance?.map((item) => new Paragraph("- " + item)) || [];
    const legalClauses = tender?.proposal?.Proposal_Template?.Legal_Clauses?.Table?.map(({ Type = '', Clause_Number = '', Clause_Title = '', Description = ''}) => new TableRow({
        children: [
            new TableCell({ children: [new Paragraph(Type)] }),
            new TableCell({ children: [new Paragraph(Clause_Number)] }),
            new TableCell({ children: [new Paragraph(Clause_Title)] }),
            new TableCell({ children: [new Paragraph(Description)] }),
        ],
    })) || [];
    const requiredDocumentationChecklist = tender?.proposal?.Proposal_Template?.Required_Documentation?.Checklist?.map((item) => new Paragraph("- " + item)) || [];

    const heading = (text: string) =>
        new Paragraph({
            text,
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 200, after: 100 },
        });

    await exportDoc([
        heading(`${Tender.proposal} #${tender?.proposal?.Proposal_Template?.Cover_Page?.Solicitation_Number}`),
        new Paragraph(""),

        heading(Tender.coverPage),
        new Paragraph({ children: [new PageBreak()] }),
        new Paragraph(Tender.solicitationNumber + ": " + tender?.proposal?.Proposal_Template?.Cover_Page?.Solicitation_Number),
        new Paragraph(Tender.tenderTitle + ": " + tender?.proposal?.Proposal_Template?.Cover_Page?.Tender_Title),
        new Paragraph(Tender.buyerInformation + ":"),
        new Paragraph(Tender.agencyName + ": " + tender?.proposal?.Proposal_Template?.Cover_Page?.Buyer_Information?.Agency_Name),
        new Paragraph(Tender.officeName + ": " + tender?.proposal?.Proposal_Template?.Cover_Page?.Buyer_Information?.Office_Name),
        new Paragraph(Tender.address + ": " + tender?.proposal?.Proposal_Template?.Cover_Page?.Buyer_Information?.Address),
        new Paragraph(`${Tender.pointOfContact}: ${tender?.proposal?.Proposal_Template?.Cover_Page?.Buyer_Information?.Point_of_Contact?.Name} (${tender?.proposal?.Proposal_Template?.Cover_Page?.Buyer_Information?.Point_of_Contact?.Email})`),
        new Paragraph(Tender.contractorInformation),
        new Paragraph(Tender.submissionDeadline + ": " + submissionDeadline),

        heading(Tender.companyIntroduction),
        new Paragraph(Tender.overview + ": " + tender?.proposal?.Proposal_Template?.Company_Introduction?.Overview),
        new Paragraph(Tender.setAsideQualification + ": " + tender?.proposal?.Proposal_Template?.Company_Introduction?.Set_Aside_Qualification),

        heading(Tender.scopeOfWork),
        new Paragraph(Tender.projectOverview + ": " + tender?.proposal?.Proposal_Template?.Scope_of_Work?.Project_Overview),
        new Paragraph(Tender.specificDeliverables + ":"),
        ...specificDeliverables,

        heading(Tender.billOfMaterials),
        new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            rows: [
                new TableRow({
                    children: [Tender.item, Tender.quantity, Tender.preferredVendor, Tender.modelNumber, Tender.technicalSpecification, Tender.unitPrice, Tender.deliveryTime].map(
                        text => new TableCell({ children: [new Paragraph({ text })] })
                    ),
                }),
                ...billOfMaterialsTable,
            ],
        }),

        heading(Tender.technicalSpecificationsCompliance),
        new Paragraph(Tender.requiredCertificates + ": " + tender?.proposal?.Proposal_Template?.Technical_Specifications_Compliance?.Required_Certificates),
        new Paragraph(Tender.standardsAndCompliance + ":"),
        ...standardsAndCompliance,

        heading(Tender.deliveryInspectionDetails),
        new Paragraph(Tender.deliveryAddress + ": " + tender?.proposal?.Proposal_Template?.Delivery_Inspection_Details?.Delivery_Address),
        new Paragraph(Tender.placeOfPerformance + ": " + tender?.proposal?.Proposal_Template?.Delivery_Inspection_Details?.Place_of_Performance),
        new Paragraph(Tender.inspections + ": " + tender?.proposal?.Proposal_Template?.Delivery_Inspection_Details?.Specific_Inspections?.join(',')),

        heading(Tender.warrantyGuarantee),
        new Paragraph(Tender.requirements + ": " + tender?.proposal?.Proposal_Template?.Warranty_Guarantee?.Requirements),

        heading(Tender.legalClauses),
        new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            rows: [
                new TableRow({
                    children: [Tender.type, Tender.clauseNumber, Tender.clauseTitle, Tender.description].map(
                        text => new TableCell({ children: [new Paragraph({ text })] })
                    ),
                }),
                ...legalClauses,
            ],
        }),

        heading(Tender.appendix),
        new Paragraph(Tender.requiredDocumentation + ":"),
        ...requiredDocumentationChecklist,

    ], tender.solicitationNumber)
}